<template>
  <div class="clinic-list" v-loader.opacity="loading">
    <div class="no-results-box" v-if="alert.message">
      <BaseNoResults :title="alert.title" :text="alert.message"/>
    </div>

    <BaseTable class="no-border" v-if="tableArchivedClinic && session && !alert.message" :tabledata="tableArchivedClinic">
      <template v-slot:column_parent="{item}">
        <span v-if="item.parent">{{ item.parent.name }}</span>
        <span v-else>{{ $t('clinic.noParentClinic') }}</span>
      </template>

      <template v-slot:column_permission="{item}">
        <span class="label" :class="getPermissionClass(item)">
          {{ $t(item.userPermission) }}
        </span>
      </template>

      <template v-slot:column_actions="{item}">
        <div class="flex-center">
          <button class="btn btn-success" type="button" name="button" @click.prevent.stop="confirmRestore(item)" v-if="item.userPermission == 'owner' && item?.canArchive">
            {{ $t('clinic.restore') }}
          </button>
        </div>
      </template>
    </BaseTable>

    <teleport :to="`#${slotPagination}`" v-if="isDataReady">
      <div v-if="tableArchivedClinic" class="align-items-center pagination-row">
        <p class="pagination-total">{{ `${tableArchivedClinic.total} ${$t("pagination.clinics")}` }}</p>
          <BasePagination :total-pages="totalPages" :total="tableArchivedClinic.total" :is-loading="loading"
                          :per-page="perPage" :current-page="currentPage" @pagechanged="onPageChange"/>
      </div>
    </teleport>

    <BaseGenericOffcanvas :offcanvas_id="'offcanvasClinicArchivedGeneric'" ref="offcanvasGeneric"/>
  </div>
</template>

<style lang="scss" scoped>
.clinic-list {
  padding:                           10px;
}

.vtable > tbody > tr > td {
  button {
    &:first-child {
      margin-right:                  5px;
    }
  }
}

.vtable-mob > ul > li p span.glyphicon {
  display:                           none;
}

@media (max-width: 400px) {
  .vtable-mob > ul > li {
    p {
      span:last-child {
        float:                       unset !important;
      }
    }
  }
}

@media (max-width: 991px) {
  .clinic-list {
    padding:                          0;
  }

  .inside-box-mob-item p {
    display:                          flex;
  }

  .clinic-list,
  .no-results-box {
    height:                          100%;
    text-align:                      center;
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'

export default {
  name: 'ClinicArchivedList',
  data: () => ({
    loading: false,
    alert: {
      title: null,
      message: null,
      class: null
    },
    tableArchivedClinic: null,
    perPage: 20,
    currentPage: 1,
    totalPages: 0,
    session: {
      collaborator: {},
      currentClinic: {}
    },
    isDataReady: false,
    slotPagination: 'settingsPagination',
  }),
  created() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo
  },
  mounted() {
    this.$activeLink.value = 'clinic'
    this.fetchData()

    this.handleResize()
    window.addEventListener('resize', this.handleResize)
  },
  methods: {
    onPageChange(page) {
      this.currentPage = page
      this.fetchData()
    },
    async fetchData() {
      this.tableArchivedClinic = null

      this.loading = true
      NProgress.start()
      try {
        // deliberately no currentClinicId here - the "pin current clinic first" logic
        // in ClinicService assumes the pinned clinic is part of the result set, which
        // would be wrong for an archived-only listing (the current clinic can't be
        // archived, so it would never belong here).
        let params = {
          page: this.currentPage,
          deleted: true
        }

        let response = await this.$clinicService.getList(params)
        this.loadTableData(response)
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      } finally {
        NProgress.done(true)
        this.loading = false
        this.isDataReady = true
      }
    },
    loadTableData(response) {
      this.alert = {}

      this.tableArchivedClinic = {
        items: response.result,
        total: response.totalCount,
        columns: [
          {name: this.$i18n.t('createClinic.name'), type: 'string', value: 'name', width: '40%'},
          {key: 'parent', name: this.$i18n.t('createClinic.parentClinic'), type: 'custom', value: 'parent.name', width: '25%'},
          {key: 'permission', name: this.$i18n.t('createClinic.permission'), type: 'custom', value: 'userPermission', width: '25%'},
          {key: "actions", name: this.$i18n.t('clinic.actions'), type: 'custom', value: 'actions', width: '10%', align: 'text-center'},
        ]
      }

      this.totalPages = Math.ceil(response.totalCount / this.perPage)

      if (response.result.length === 0) {
        this.tableArchivedClinic = {}
        this.totalPages = 1
        this.alert = this.$message.alertWithTitle(this, 'request_notfound_archived_clinic_title', 'request_notfound_archived_clinic_text')
      }
    },
    getPermissionClass(item) {
      if (item.userPermission == 'admin') {
        return 'label-blue'
      } else if (item.userPermission == 'physician') {
        return 'label-green'
      } else if (item.userPermission == 'technician') {
        return 'label-purple'
      } else if (item.userPermission == 'specialist') {
        return 'label-warning'
      } else {
        return 'label-pink'
      }
    },
    handleResize() {
      this.slotPagination = window.innerWidth > 767 ? 'settingsPagination' : 'settingsPaginationMobile'
    },
    async confirmRestore(item) {
      const ok = await this.$refs.offcanvasGeneric.show({
        title: this.$i18n.t('confirm'),
        text: this.$i18n.t('clinic.confirmClinicRestore'),
        okButton: this.$i18n.t('clinic.restore'),
        okButtonClass: 'btn-success'
      })
      if (ok) {
        this.doRestore(item)
      }
    },
    async doRestore(item) {
      this.loading = true
      NProgress.start()
      try {
        let params = {
          clinic: {
            ...item,
            deleted: false
          }
        }
        await this.$clinicService.updateClinic(params)
        this.$message.popup(this, 'general.updatedSuccess', 'operationSuccess', 'success')

        this.fetchData()
      } catch (err) {
        this.$message.popup(this, 'general.updatedError', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
  },
  unmounted() {
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>
